import React, {useRef, useState} from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Typography, IconButton, Tooltip } from '@material-ui/core';
import { AddBox, LinkRounded } from '@material-ui/icons';
import UnpairedProductList from './UnpairedProductList';
import UnpairedProductForceCreate from './UnpairedProductForceCreate';
import { hasPermission, createHistory } from '../../sc-utilities';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const UnpairedProducts = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, productExternalInfoID: null, redirectPath: '' });
    const [forceCreateModalState, setForceCreateModalState] = useState({
        open: false,
        productExternalInfoID: null,
        productExternalInfoIDs: [],
        forBulk: false
    });

    let visible = columns && columns['UnpairedProduct'];

    const tableActionsRow = [
        {
            icon: 'AddToExisting',
            tooltip: translations['assign_to_existing'] || 'Assign to existing',
            onClick: (event, rowData) => goToAddToExisting(event, rowData),
            hidden: !hasPermission(permissions, 'unpaired_product_assign')
        },
        {
            icon: 'ForceCreate',
            tooltip: translations['force_create'] || 'Force create',
            onClick: (event, rowData) => handleForceCreate(event, rowData),
            hidden: !hasPermission(permissions, 'unpaired_product_force_create')
        }
    ];

    const getIcon = icon => {
        switch (icon) {
            case 'AddToExisting':
                return <AddBox />
            case 'ForceCreate':
                return <LinkRounded />
            default:
                return <> </>
        }
    };

    const tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 150, maxWidth: 150 },
            filtering: false,
            render: rowData => {
                return tableActionsRow.map(action => {
                    if (action.hidden) {
                        return null;
                    }
                    return (
                        <Tooltip key={`${action.icon}-${rowData.ProductExternalInfoID}`} title={action.tooltip}>
                            <IconButton
                                component='div'
                                onClick={(event) => action.onClick(event, rowData)}
                            >
                                {
                                    getIcon(action.icon)
                                }
                            </IconButton>
                        </Tooltip>
                    );
                });
            }
        },
        {
            title: translations["external_service_type"] || 'External service type', field: 'ExternalServiceType',
            hidden: visible ? visible['ExternalServiceType'] : false,
            lookup: { 'staging_v1': translations['staging_v1'] || 'Staging V1', 'asset_v1': translations['asset_v1'] || 'Asset V1' },
            defaultFilter: ['staging_v1', 'asset_v1'],
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ExternalServiceType === 'staging_v1' ? translations['staging_v1'] || 'Staging V1' : translations['asset_v1'] || 'Asset V1'}</Typography>;
            }
        },
        {
            title: translations["type"] || 'Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            lookup: { 'MobileProduct': translations['mobile_product_unpaired'] || 'Mobile Product', 'PCProduct': translations['pc_product_unpaired'] || 'PC Product' },
            defaultFilter: ['MobileProduct', 'PCProduct'],
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Product.Type === 'MobileProduct' ? translations['mobile_product_unpaired'] || 'Mobile Product' : translations['pc_product_unpaired'] || 'PC Product'}</Typography>;
            }
        },
        {
            title: translations["inventory_id"] || 'Securaze ID', field: 'InventoryID',
            hidden: visible ? visible['InventoryID'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Product.InventoryID ? rowData.Product.InventoryID : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["vendor"] || 'Vendor', field: 'Vendor',
            hidden: visible ? visible['Vendor'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Product.Vendor ? rowData.Product.Vendor : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["model"] || 'Model', field: 'Model',
            hidden: visible ? visible['Model'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Product.Type === "MobileProduct" ? rowData.Product && rowData.Product.MobileProduct ? rowData.Product.MobileProduct.MobileProductModel : translations['short_not_assigned'] || 'n/a' : rowData.Product.Type === "PCProduct" ? rowData.Product && rowData.Product.PCProduct ? rowData.Product.PCProduct.Name : translations['short_not_assigned'] || 'n/a' : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["serial_number"] || 'Serial number', field: 'SerialNumber',
            hidden: visible ? visible['SerialNumber'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Product.SerialNumber ? rowData.Product.SerialNumber : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        }
    ];

    const goToAddToExisting = (_event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, productExternalInfoID: rowData.ProductExternalInfoID, redirectPath: `/unpairedAssets/unpairedProducts-assignToExisting?ProductExternalInfoID=${rowData.ProductExternalInfoID}` });
    };

    const handleForceCreate = async (_event, rowData) => {
        const productExternalInfoID = rowData.ProductExternalInfoID;
        setForceCreateModalState({ ...forceCreateModalState, open: true, productExternalInfoID: productExternalInfoID });
    };

    const handleBulkForceCreate = productExternalInfoIDS => {
        setForceCreateModalState({ ...forceCreateModalState, open: true, forBulk: true, productExternalInfoIDs: productExternalInfoIDS });
    };

    const redirectHandler = (path, productExternalInfoID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, productExternalInfoID: productExternalInfoID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    const tableActions = [
        {
            tooltip: translations["bulk_force_create"] || "Bulk force create",
            icon: <LinkRounded />,
            onClick: (rowData) => handleBulkForceCreate(rowData.map(unpairedProduct => unpairedProduct.ProductExternalInfoID))
        }
    ];

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'unpaired_product_list') ?
                    <UnpairedProductList
                        tableColumns={tableColumns}
                        redirectHandler={redirectHandler}
                        tableActions={tableActions}
                        tableRef={tableRef}
                    /> : null
            }
            {
                hasPermission(permissions, 'unpaired_product_force_create') ?
                    <UnpairedProductForceCreate
                        forceCreateModalState={forceCreateModalState}
                        setForceCreateModalState={setForceCreateModalState}
                    /> : null
            }
        </div >
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles, { withTheme: true })(UnpairedProducts));